import { styled } from "@linaria/react";
import { useAvivaSelector } from "@redux/hook";
import { GAAviva } from "@utils/gaAvivaR3";
import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import Listing, { Source } from "../Listing";
import _, { get } from "lodash";
import { PLANS_NAME } from "@static/config";
import { useDispatch } from "react-redux";
import { generatePlanOverView } from "@pages/ShieldStarter/Helpers/generatePlanOverView";
import { getPlanOverView } from "@utils/getPlanOverview";
import { themeDefault } from "src/theme-revamp";
import { DEFAULT_DISCLAIMER_MSG, DISCLAIMER_TEXT } from "@static/content";
import { MLTC_DISCLAIMER_MIN_ANNUAL_PREMIUM } from "@static/staticConstents";
import { EverGreenStyle } from "@utils/Style/everGreen";
import { store } from "@redux/store";
import { ROUTE_PATH } from "@static/route-paths";
import { Helpers } from "@utils/helpers";
import { trackOnCTA } from "@pages/CCP/Helpers/analyticsHelper";

const NoplanButton = styled.button<{ primaryColor?: string }>`
  display: inline-block;
  background-color: ${(props) => props.primaryColor || "#883e89"};
  padding: 15px 50px 15px 50px;
  cursor: pointer;
  border: none;
  border-radius: 100rem;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  letter-spacing: 0.08em;
  max-width: 322px;
`;

const NoplanHeading = styled.h4<{ primaryColor?: string }>`
  color: ${(props) => props.primaryColor || "#883e89"};
  text-align: center;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
  margin-bottom: 32px;
`;

const NoplanContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: auto;
  padding: 24px 49px 24px 49px;
`;

const HoverContent = styled.div`
  background-color: #ffffff;
  border-radius: 0 0 16px 16px;
`;

const HoverHeading = styled.h1`
  color: #ffffff;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
`;

const HoderHeader = styled.div<{ primaryColor?: string }>`
  background-color: ${(props) => props.primaryColor || "#883e89"};
  padding: 20px 36px 20px 36px;
  border-radius: 16px 16px 0 0;
`;

const HoverInfo = styled.div`
  padding-top: 28px;
  display: inline-block;
  width: 435px;
  position: absolute;
  top: 100%;
  left: -86px;
  box-shadow: 0px 25px 32px rgba(0, 0, 0, 0.1);
  border-radius: 0 0 16px 16px;
  &:after {
  }
  @media screen and (max-width: 768px) {
    left: 0;
  }
`;
const PlanIconArrow = styled.div<{ primaryColor?: string }>`
  content: "";
  width: 16px;
  height: 16px;
  background-color: ${(props) => props.primaryColor || "#883e89"};
  position: absolute;
  top: 20px;
  left: 86px;
  transform: rotate(45deg);
`;

export interface PlanOverviewProps {
  variant: "noplan" | "haveplan";
  primaryColor?: string;
}

export const PlanOverview = ({
  variant,
  primaryColor,
  ...props
}: PlanOverviewProps) => {
  const history = useHistory();
  const CurrentPolicyFlow = useAvivaSelector(
    (state) => state.loading.CurrentPolicyFlow.policy
  );
  const planSelection = useAvivaSelector((state) => {
    switch (CurrentPolicyFlow) {
      case PLANS_NAME.SHIELD_STARTER: {
        return state.shieldStarterStep2.policyName;
      }
      case PLANS_NAME.EVER_GREEN: {
        return state.everGreenStep1.quotation.investingAmount > 0;
      }
      case PLANS_NAME.CANCER_COVER_PLUS: {
        return (
          state.cancerCoverStep1.quotation?.totalPremium &&
          state.cancerCoverStep1.quotation?.totalPremium > 0
        );
      }
      default: {
        return state.mltcR3Step2.planSelection;
      }
    }
  });
  const planOverview = useAvivaSelector(
    (state) =>
      getPlanOverView(state, CurrentPolicyFlow) ||
      state.mltcR3Step2.planOverview
  );
  const sqsQuotation = useAvivaSelector(
    (state) => state.mltcR3Step2.sqsQuotation
  );
  const shieldStarterPlanOverview = useAvivaSelector(
    (state) => state.shieldStarterStep2.planOverview
  );
  const adviser = useAvivaSelector(
      (state) => state.cancerCoverStep6.financialAdviser.adviserData
    );
  const isPromoCodeValid = 'validPromoCode' in adviser &&  adviser?.validPromoCode === "Y";
  const [isLoaded, setLoaded] = useState<boolean>(false);
  const [tempPlanOverview, setTempPlanOverview] = useState<any>(
    _.cloneDeep(planOverview)
  );
  const rawQuotation = useAvivaSelector(
    (state) => state.shieldStarterStep2.rawQuotaion
  );
  const planContainerRef = useRef<null | HTMLDivElement>(null);
  const planContainerIconRef = useRef<null | HTMLDivElement>(null);
  const isShieldStarter = CurrentPolicyFlow === PLANS_NAME.SHIELD_STARTER;
  const isMLTC = CurrentPolicyFlow === PLANS_NAME.MLTC;

  useEffect(() => {
    const handleResize = () => {
      calculateContainerPosition();
    };
    window.addEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    calculateContainerPosition();
    if (!isLoaded) {
      setLoaded(true);
    }
  }, [isLoaded]);

  const calculateContainerPosition = () => {
    if (planContainerRef.current && planContainerIconRef.current) {
      let bufferSpace = 20;
      if (
        planContainerRef.current.getBoundingClientRect().right >
        window.innerWidth
      ) {
        let different =
          planContainerRef.current.getBoundingClientRect().right -
          window.innerWidth +
          bufferSpace;
        let leftValue = Math.abs(planContainerRef.current.offsetLeft);
        planContainerRef.current.style.left =
          "-" + (leftValue + different) + "px";

        let IconleftValue = Math.abs(planContainerIconRef.current.offsetLeft);
        planContainerIconRef.current.style.left =
          IconleftValue + different + "px";
      }
    }
  };

  let planOverviewList: Source[] = [
    {
      heading: { text: "Plan overview" },
      descriptions: [
        { text: "Plan Selected", value: "Singlife CareShield Standard" },
        { text: "Monthly Benefit", value: "-" },
      ],
    },
    {
      heading: { text: "Premium Breakdown" },
      descriptions: [
        {
          text: "Annual Premium",
          value: "-",
        },
        {
          text: '<p class="insertPromoTextClass">Promo Code:</p><p><span style="font-weight:600;">LTC1</span> (20% off applied)</p>',
          value: "-",
        },
        {
          text: "Cpf MediSave deduction",
          value: "-",
        },
      ],
    },
  ];

  let summary: any = { text: "Cash Payment", value: "-" };
  let subTotal;
  if (CurrentPolicyFlow === PLANS_NAME.SHIELD_STARTER) {
    const shieldStarter = shieldStarterPlanOverview;

    planOverviewList = shieldStarter.planOverviewList;
    summary = shieldStarter.summary;
    subTotal = shieldStarter.subTotal;
  } else if (CurrentPolicyFlow === PLANS_NAME.EVER_GREEN) {
    const PlanData = store.getState().everGreenStep1.PlanData;
    if (PlanData) {
      planOverviewList = PlanData?.planoverview;
      summary = PlanData?.summary;
    }
  } else if (CurrentPolicyFlow === PLANS_NAME.CANCER_COVER_PLUS) {
    const planData = store.getState().cancerCoverStep1.quotation;
    planOverviewList = [
      {
        heading: { text: "" },
        descriptions: [
          {
            text: "Plan",
            value:
              "<p style='white-space: nowrap;'>Singlife Cancer Cover Plus II</p>",
          },
          {
            text: "Buying for",
            value:
              store.getState().cancerCoverStep1.basicInfo?.forwhom === "child"
                ? "My Child"
                : "Myself",
          },
          {
            text: "Date of birth",
            value: Helpers.parseDobString(
              store.getState().cancerCoverStep1.basicInfo?.dob
            ).replaceAll("-", " "),
          },
        ],
      },
    ];
    summary = [
      {
        text: `<p style='font-size: 14px;font-weight: 500;line-height: 22.4px;text-align: left;'>Subtotal Premium</p>`,
        value: `<p style='font-size: 14px;font-weight: 600;line-height: 22.4px;text-align: right;'>${
          store.getState().cancerCoverStep1?.quotation?.annualizePremium
            ?`S$${ Helpers.toCurrency(
                store.getState().cancerCoverStep1?.quotation?.annualizePremium as number
              )}`
            : "-"
        }</p>`,
      },
     {
            text: `<p style='font-size: 14px;font-weight: 500;line-height: 22.4px;text-align: left;'>${store.getState().cancerCoverStep1?.basicInfo?.paymentOption==="MP"?`Monthly `:``}Discount ${store.getState().cancerCoverStep1.basicInfo?.promoCode && !isPromoCodeValid
              ?  `(${store.getState().cancerCoverStep1.basicInfo?.promoCode})` : ""
            }</p>`,
            value: `<p style='font-size: 14px;font-weight: 600;line-height: 22.4px;text-align: right;'>${
              store.getState().cancerCoverStep1?.quotation?.premiumDiscount
                ? "-" + "S$"+
                  Helpers.toCurrency(
                    store.getState().cancerCoverStep1?.quotation
                      ?.premiumDiscount as number
                  )
                : "-"
            }</p>`,
          },
      {
        text: `<p style='font-size: 18px;font-weight: 600;line-height: 22.4px;text-align: left;color: ${
          themeDefault.colors.primary
        }'>${
          store.getState().cancerCoverStep1.basicInfo?.paymentOption === "AP"
            ? "Annual"
            : "Monthly"
        } Premium</p>`,
        value: `<p style='font-size: 18px;font-weight: 600;line-height: 22.4px;text-align: left;color: ${
          themeDefault.colors.primary
        }'>S$${
          store.getState().cancerCoverStep1?.quotation?.firstYrAnnualizedPremium
            ? Helpers.toCurrency(
                store.getState().cancerCoverStep1?.quotation
                  ?.totalPremium as number
              )
            : store.getState().cancerCoverStep1?.quotation?.annualizePremium ? Helpers.toCurrency(
              store.getState().cancerCoverStep1?.quotation
                ?.totalPremium as number
            ) : "-"
        }</p>`,
      },
    ].filter((s) => Boolean(s));
  } else {
    planOverviewList =
      tempPlanOverview?.quoteBreakdown && sqsQuotation
        ? tempPlanOverview?.quoteBreakdown
        : planOverviewList;
    summary =
      tempPlanOverview?.summary && sqsQuotation
        ? tempPlanOverview?.summary
        : summary;
  }

  const promoApplied =
    useAvivaSelector(
      (state) =>
        state?.mltcR3Step2?.sqsQuotation?.coverages[0]?.attributes
          ?.promoCodeSummary
    ) || "";
  let disclaimerContentArray: string | string[] = [];
  const monthlyBenifitAmount = useAvivaSelector(
    (state) =>
      state.mltcR3Step2.quoteRawResponse?.coverages[0]?.attributes
        ?.nonDiscountedTotalPremium
  );
  if (CurrentPolicyFlow === PLANS_NAME.MLTC) {
    disclaimerContentArray = [DEFAULT_DISCLAIMER_MSG];
    if (
      promoApplied?.includes("DBS") &&
      monthlyBenifitAmount >= MLTC_DISCLAIMER_MIN_ANNUAL_PREMIUM
    ) {
      disclaimerContentArray.push(DISCLAIMER_TEXT);
    }
    summary = tempPlanOverview?.summary?.subtext && {
      ...tempPlanOverview?.summary,
      isHtml: true,
      subtext: `<p style="width:230px;font-size:12px">${tempPlanOverview?.summary?.subtext}</p>`,
    };
  } else if (CurrentPolicyFlow === PLANS_NAME.SHIELD_STARTER) {
    disclaimerContentArray =
      "*Refer to plan summary for breakdown and payment amount for subsequent years premium";
  }

  function stylePropsCalc() {
    switch (CurrentPolicyFlow) {
      case PLANS_NAME.EVER_GREEN: {
        return {
          top: `16px`,
          bottom: `16px`,
          summaryColor: EverGreenStyle.colors.primary,
          summaryTextFontSize: "18px",
        };
      }
      case PLANS_NAME.MLTC: {
        return {
          top: `${36 / 16}rem`,
          bottom: `${36 / 16}rem`,
          summaryColor: themeDefault.colors.primary,
          summaryTextFontSize: "16px",
        };
      }
      case PLANS_NAME.SHIELD_STARTER: {
        return {
          top: `${36 / 16}rem`,
          bottom: `${21 / 16}rem`,
          summaryColor: "",
          summaryTextFontSize: undefined,
        };
      }
      default: {
        return {
          top: `${36 / 16}rem`,
          bottom: `${21 / 16}rem`,
          summaryColor: themeDefault.colors.grey[500],
          summaryTextFontSize: "16px",
        };
      }
    }
  }

  const styleProps = stylePropsCalc();

  const trackCTA = () => {
    const GA = GAAviva.getInstance();
    let { cta } = GA.digitalData;
    cta = {
      type: "Header Navigation",
      text: "Get a quote",
    };
    GA.trackCTAClick({ cta });
  };

  const getQuoteOnClick = () => {
    if (CurrentPolicyFlow === PLANS_NAME.CANCER_COVER_PLUS) {
      trackOnCTA({
        ctaText: "Get a quote",
        ctaType: "qb:link",
      });
      return;
    }
    trackCTA();
    if (CurrentPolicyFlow === PLANS_NAME.EVER_GREEN) {
      history.push(ROUTE_PATH.EVER_GREEN_QUOTATION);
      return;
    }
    history.push("/mltc/lets-get-started");
  };

  useEffect(() => {
    if (planOverview && !isLoaded) {
    }
  }, [planOverview, isLoaded]);
  const isValidPromoCode = useAvivaSelector(
    (state) => state.mltcR3Step5.validPromoCode
  );
  useEffect(() => {
    if (tempPlanOverview && isMLTC) {
      if (tempPlanOverview?.quoteBreakdown[1]?.descriptions[1]) {
        let quoteBreakdownArr =
          tempPlanOverview.quoteBreakdown[1].descriptions[1].text
            .replaceAll("</p>", "</p>|")
            .split("|");
        quoteBreakdownArr.pop();
        quoteBreakdownArr.shift();
        tempPlanOverview.quoteBreakdown[1].descriptions[1].text = isValidPromoCode === "Y" ? tempPlanOverview.quoteBreakdown[1].descriptions[1].text : `<p>Promo Code:</p><p>${quoteBreakdownArr.join(
          ""
        )}</p>`;
      } else {
        tempPlanOverview.quoteBreakdown[1].descriptions.splice(1, 1);
      }
    }
  }, [tempPlanOverview, isMLTC]);
  return (
    <HoverInfo className={"PlanOverviewContainer"} ref={planContainerRef}>
      <HoderHeader primaryColor={primaryColor}>
        <HoverHeading>Plan Overview</HoverHeading>
      </HoderHeader>
      <HoverContent>
        {!planSelection ||
        !planOverview ||
        (globalThis.location.hash.includes("mltc") &&
          get(planOverviewList, "1.descriptions.0.value", "-") === "-") ||
        globalThis.location.hash.includes(ROUTE_PATH.EVER_GREEN_THANK_YOU) ? (
          <NoplanContainer>
            <NoplanHeading primaryColor={primaryColor}>
              You currently have no plan selections
            </NoplanHeading>
            <NoplanButton primaryColor={primaryColor} onClick={getQuoteOnClick}>
              Get a quote
            </NoplanButton>
          </NoplanContainer>
        ) : (
          <div
            style={{
              display: `${
                [PLANS_NAME.SHIELD_STARTER, PLANS_NAME.EVER_GREEN].includes(
                  CurrentPolicyFlow as PLANS_NAME
                )
                  ? "block"
                  : "inline-block"
              }`,
              padding: "24px 36px 24px 36px",
              width: "-webkit-fill-available",
            }}
          >
            <Listing
              variant={"summarization"}
              margin={"0"}
              subTotal={subTotal}
              summaryTextColor={styleProps.summaryColor}
              headerMaxValueWidth={"0"}
              headingColor={
                [PLANS_NAME.EVER_GREEN].includes(
                  CurrentPolicyFlow as PLANS_NAME
                )
                  ? EverGreenStyle.colors.primary
                  : themeDefault.colors.primary
              }
              headingFontSize={`${
                [PLANS_NAME.SHIELD_STARTER, PLANS_NAME.EVER_GREEN].includes(
                  CurrentPolicyFlow as PLANS_NAME
                )
                  ? "18px"
                  : ""
              }`}
              sources={planOverviewList}
              summary={summary as any}
              summaryTextFontSize={styleProps.summaryTextFontSize}
              dividerMarginTop={styleProps.top}
              dividerMarginBottom={styleProps.bottom}
              showDisclaimer={
                isMLTC ? true : sqsQuotation?.firstYearDiscountEligible
              }
              alignDescriptionItems="flex-start"
              disclaimerText={disclaimerContentArray || DISCLAIMER_TEXT}
            />
          </div>
        )}
      </HoverContent>
      <PlanIconArrow primaryColor={primaryColor} ref={planContainerIconRef} />
    </HoverInfo>
  );
};
